import { getDefaultConsigneeValue, ConsigneeValue } from "@RequestFormV2/defaults/consigneeDefaults";
import {
  getDefaultNotifyPartyValue,
  NotifyPartySyncOption,
  NotifyPartyValue,
} from "@RequestFormV2/defaults/notifyPartyDefaults";
import {
  getDefaultDeliveryStopValue,
  getDefaultScheduleStops,
  ScheduleStopValue,
} from "@RequestFormV2/defaults/scheduleDefaults";
import { StopAddressType, StopTimeType } from "types/MultiStop";

type FormAddressContactType = NotifyPartyValue["form_address_contact_type"];

function mapFormAddressContactTypeToNotifyPartySync(type: FormAddressContactType | undefined): NotifyPartySyncOption {
  switch (type) {
    case "delivery":
      return "sameAsDeliveryAddressContact";
    case "consignee":
      return "sameAsConsigneeAddressContact";
    default:
      return "customAddressContact";
  }
}

/**
 * Draft API persists `form_address_contact_type` but often omits UI sync fields
 * (`same_as_address_contact` / `same_as_delivery_address_contact`). Derive those
 * so radios/checkboxes match the loaded draft.
 */
function normalizeConsigneeFromDraft(raw: Partial<ConsigneeValue> | undefined): ConsigneeValue {
  const merged: ConsigneeValue = {
    ...getDefaultConsigneeValue(),
    ...(raw ?? {}),
  };

  if (raw && !("same_as_delivery_address_contact" in raw)) {
    merged.same_as_delivery_address_contact = merged.form_address_contact_type === "delivery";
  }

  return merged;
}

function normalizeNotifyPartyFromDraft(raw: Partial<NotifyPartyValue> | undefined): NotifyPartyValue {
  const merged: NotifyPartyValue = {
    ...getDefaultNotifyPartyValue(),
    ...(raw ?? {}),
  };

  if (raw && !("same_as_address_contact" in raw)) {
    merged.same_as_address_contact = mapFormAddressContactTypeToNotifyPartySync(merged.form_address_contact_type);
  }

  return merged;
}

type ClassicStopPrefix = "pickup" | "stop1";

function pickFirstDefined(...values: unknown[]): unknown {
  return values.find((value) => value !== undefined && value !== null);
}

function normalizeClassicTime(time: unknown): string | null {
  if (time === undefined || time === null || time === "") return null;
  const timeStr = String(time);
  const match = timeStr.match(/^(\d{2}:\d{2}:\d{2})/);
  return match ? match[1] : timeStr;
}

function mapClassicAddressType(value: unknown): StopAddressType | undefined {
  if (value === undefined || value === null || value === "") return undefined;

  if (typeof value === "number") {
    return value as StopAddressType;
  }

  switch (String(value).toLowerCase()) {
    case "airport":
      return StopAddressType.AIRPORT;
    case "seaport":
    case "port":
      return StopAddressType.SEAPORT;
    case "address":
    default:
      return StopAddressType.ADDRESS;
  }
}

function mapClassicCargoReady(value: unknown, isPickup: boolean): StopTimeType | undefined {
  if (value === undefined || value === null || value === "" || value === "unspecified") {
    return undefined;
  }

  const cargoReady = String(value).toLowerCase();

  if (isPickup) {
    if (cargoReady === "after" || cargoReady === "asap_pickup") return StopTimeType.AFTER;
    if (cargoReady === "between") return StopTimeType.BETWEEN;
    return undefined;
  }

  if (cargoReady === "before") return StopTimeType.BEFORE;
  if (cargoReady === "between") return StopTimeType.BETWEEN;
  if (cargoReady === "asap_delivery") return StopTimeType.ASAP_DELIVERY;
  return undefined;
}

function getClassicValue(schedule: Record<string, unknown>, prefix: ClassicStopPrefix, suffix: string): unknown {
  return schedule[`${prefix}_${suffix}`];
}

function assignIfDefined(stop: Record<string, unknown>, field: string, value: unknown) {
  if (value !== undefined && value !== null) {
    stop[field] = value;
  }
}

function mapClassicStop(
  schedule: Record<string, unknown>,
  prefix: ClassicStopPrefix,
  defaultStop: ScheduleStopValue,
): ScheduleStopValue {
  const isPickup = prefix === "pickup";
  const stop: Record<string, unknown> = { ...defaultStop };

  const addressType = mapClassicAddressType(getClassicValue(schedule, prefix, "address_type"));
  if (addressType !== undefined) {
    stop.stop_address_type = addressType;
  }

  assignIfDefined(
    stop,
    "address_name",
    pickFirstDefined(getClassicValue(schedule, prefix, "address_name"), getClassicValue(schedule, prefix, "company_name")),
  );
  assignIfDefined(stop, "street_address", getClassicValue(schedule, prefix, "street"));
  assignIfDefined(stop, "street_address_line_two", getClassicValue(schedule, prefix, "street_line2"));
  assignIfDefined(stop, "zipcode", getClassicValue(schedule, prefix, "zipcode"));
  assignIfDefined(stop, "city_name", getClassicValue(schedule, prefix, "city"));
  assignIfDefined(stop, "state_name", getClassicValue(schedule, prefix, "state"));
  assignIfDefined(stop, "country_name", getClassicValue(schedule, prefix, "country"));
  assignIfDefined(stop, "stop_contact_name", getClassicValue(schedule, prefix, "contact_name"));
  assignIfDefined(stop, "stop_contact_number", getClassicValue(schedule, prefix, "contact_phone"));
  assignIfDefined(stop, "stop_contact_email", getClassicValue(schedule, prefix, "contact_email"));
  assignIfDefined(stop, "stop_notes", getClassicValue(schedule, prefix, "notes"));
  assignIfDefined(stop, "stop_timezone", getClassicValue(schedule, prefix, "timezone"));
  assignIfDefined(stop, "stop_airport_name", getClassicValue(schedule, prefix, "airport"));
  assignIfDefined(stop, "stop_airport_code", getClassicValue(schedule, prefix, "airport_code"));
  assignIfDefined(stop, "stop_seaport_name", getClassicValue(schedule, prefix, "seaport"));
  assignIfDefined(stop, "stop_seaport_code", getClassicValue(schedule, prefix, "seaport_code"));
  assignIfDefined(stop, "un_locode", getClassicValue(schedule, prefix, "un_locode"));
  assignIfDefined(stop, "location_reference_number", getClassicValue(schedule, prefix, "location_reference_number"));
  assignIfDefined(stop, "custom_address_reference", getClassicValue(schedule, prefix, "custom_address_reference"));

  if (isPickup) {
    assignIfDefined(
      stop,
      "address_reference_number",
      pickFirstDefined(getClassicValue(schedule, prefix, "address_reference_number")),
    );
    assignIfDefined(stop, "address_id", getClassicValue(schedule, prefix, "address_pk"));
    assignIfDefined(
      stop,
      "stop_after_date",
      pickFirstDefined(getClassicValue(schedule, prefix, "ready_date"), getClassicValue(schedule, prefix, "after_date")),
    );
    assignIfDefined(
      stop,
      "stop_after_time",
      normalizeClassicTime(
        pickFirstDefined(getClassicValue(schedule, prefix, "ready_time"), getClassicValue(schedule, prefix, "after_time")),
      ),
    );
    assignIfDefined(stop, "stop_before_date", getClassicValue(schedule, prefix, "before_date"));
    assignIfDefined(stop, "stop_before_time", normalizeClassicTime(getClassicValue(schedule, prefix, "before_time")));
    assignIfDefined(stop, "stop_hours_start", getClassicValue(schedule, prefix, "hours_from"));
    assignIfDefined(stop, "stop_hours_end", getClassicValue(schedule, prefix, "hours_to"));

    const stopTimeType = mapClassicCargoReady(schedule.pickup_cargo_ready, true);
    if (stopTimeType !== undefined) {
      stop.stop_time_type = stopTimeType;
    }

    if (stop.stop_time_type === StopTimeType.AFTER) {
      if (!getClassicValue(schedule, prefix, "before_date")) {
        stop.stop_before_date = stop.stop_after_date;
      }
      if (!getClassicValue(schedule, prefix, "before_time")) {
        stop.stop_before_time = stop.stop_after_time;
      }
    }
  } else {
    assignIfDefined(
      stop,
      "address_reference_number",
      pickFirstDefined(
        getClassicValue(schedule, prefix, "address_reference_number"),
        schedule.destination_address_reference_number,
      ),
    );
    assignIfDefined(
      stop,
      "address_id",
      pickFirstDefined(getClassicValue(schedule, prefix, "address_pk"), schedule.destination_address_pk),
    );
    assignIfDefined(
      stop,
      "stop_after_date",
      pickFirstDefined(getClassicValue(schedule, prefix, "after_date"), getClassicValue(schedule, prefix, "date")),
    );
    assignIfDefined(
      stop,
      "stop_after_time",
      normalizeClassicTime(
        pickFirstDefined(getClassicValue(schedule, prefix, "after_time"), getClassicValue(schedule, prefix, "time")),
      ),
    );
    assignIfDefined(stop, "stop_before_date", getClassicValue(schedule, prefix, "before_date"));
    assignIfDefined(stop, "stop_before_time", normalizeClassicTime(getClassicValue(schedule, prefix, "before_time")));
    assignIfDefined(stop, "stop_hours_start", schedule.delivery_hours_from);
    assignIfDefined(stop, "stop_hours_end", schedule.delivery_hours_to);

    const stopTimeType = mapClassicCargoReady(schedule.delivery_cargo_ready, false);
    if (stopTimeType !== undefined) {
      stop.stop_time_type = stopTimeType;
    }

    if (stop.stop_time_type === StopTimeType.BEFORE) {
      if (!getClassicValue(schedule, prefix, "before_date")) {
        stop.stop_before_date = stop.stop_after_date;
      }
      if (!getClassicValue(schedule, prefix, "before_time")) {
        stop.stop_before_time = stop.stop_after_time;
      }
    }
  }

  return stop as unknown as ScheduleStopValue;
}

function mirrorStopDateTimeFields(stop: ScheduleStopValue): ScheduleStopValue {
  const afterDate = stop.stop_after_date || stop.stop_before_date || null;
  const beforeDate = stop.stop_before_date || stop.stop_after_date || null;
  const afterTime = stop.stop_after_time || stop.stop_before_time || null;
  const beforeTime = stop.stop_before_time || stop.stop_after_time || null;

  return {
    ...stop,
    stop_after_date: afterDate,
    stop_before_date: beforeDate,
    stop_after_time: afterTime,
    stop_before_time: beforeTime,
  };
}

/** API stop payloads sometimes use `stop_*` address keys; V2 form fields omit that prefix. */
const STOP_ADDRESS_ALIAS_MAP: Record<string, keyof ScheduleStopValue> = {
  stop_address_name: "address_name",
  stop_street: "street_address",
  stop_street_line2: "street_address_line_two",
  stop_zipcode: "zipcode",
  stop_city_name: "city_name",
  stop_state_name: "state_name",
  stop_country_name: "country_name",
};

function isEmptyStopFieldValue(value: unknown): boolean {
  return value === undefined || value === null || value === "";
}

/**
 * Map in-stop API aliases onto V2 field names when the V2 field is empty.
 * Does not read classic flat `pickup_*` / `stop1_*` schedule fields.
 */
export function normalizeStopAddressAliases(stop: Record<string, unknown> | ScheduleStopValue): ScheduleStopValue {
  const normalized: Record<string, unknown> = { ...stop };

  Object.entries(STOP_ADDRESS_ALIAS_MAP).forEach(([aliasKey, formField]) => {
    const aliasValue = normalized[aliasKey];
    if (isEmptyStopFieldValue(normalized[formField]) && !isEmptyStopFieldValue(aliasValue)) {
      normalized[formField] = aliasValue;
    }
    delete normalized[aliasKey];
  });

  return normalized as ScheduleStopValue;
}

export function normalizeDraftSchedule(schedule: Record<string, unknown> | undefined) {
  const defaultStops = getDefaultScheduleStops();

  if (!schedule) {
    return {
      stops: defaultStops,
      consignee: getDefaultConsigneeValue(),
      notify_party: getDefaultNotifyPartyValue(),
      multistop_instructions: "",
    };
  }

  let stops: ScheduleStopValue[];

  if (Array.isArray(schedule.stops) && schedule.stops.length >= 2) {
    // Usable stops[] → V2 uses stop data only (no classic flat overlay).
    stops = (schedule.stops as Record<string, unknown>[]).map((rawStop, index) => {
      const defaultStop = index === 0 ? defaultStops[0] : getDefaultDeliveryStopValue();
      return normalizeStopAddressAliases({ ...defaultStop, ...rawStop });
    });
  } else {
    // Legacy / templates: build stops from classic flat pickup_* / stop1_* fields.
    stops = [mapClassicStop(schedule, "pickup", defaultStops[0]), mapClassicStop(schedule, "stop1", defaultStops[1])];
  }

  const consignee = normalizeConsigneeFromDraft(schedule.consignee as Partial<ConsigneeValue> | undefined);
  const notifyParty = normalizeNotifyPartyFromDraft(schedule.notify_party as Partial<NotifyPartyValue> | undefined);

  return {
    stops: stops.map(mirrorStopDateTimeFields),
    consignee,
    notify_party: notifyParty,
    multistop_instructions: schedule.multistop_instructions == null ? "" : String(schedule.multistop_instructions),
  };
}

function normalizeServiceForForm(service: Record<string, unknown> | undefined): Record<string, unknown> | undefined {
  if (!service) return service;

  return {
    ...service,
    incoterm_instructions: service.incoterm_instructions ?? "",
  };
}

function normalizeShipmentForForm(shipment: Record<string, unknown> | undefined): Record<string, unknown> | undefined {
  if (!shipment) return shipment;

  return {
    ...shipment,
    title: shipment.title ?? "",
    comments: shipment.comments ?? "",
  };
}

function normalizeInvitedProvidersForForm(
  invitedProviders: Record<string, unknown> | undefined,
): Record<string, unknown> | undefined {
  if (!invitedProviders) return invitedProviders;

  const providers = Array.isArray(invitedProviders.providers)
    ? invitedProviders.providers.map((entry) => String(entry))
    : invitedProviders.providers;

  const parcelAccounts = Array.isArray(invitedProviders.parcel_accounts)
    ? invitedProviders.parcel_accounts.map((account) => {
        if (!account || typeof account !== "object") return account;
        const item = account as Record<string, unknown>;
        if (item.provider_id == null) return item;
        return { ...item, provider_id: String(item.provider_id) };
      })
    : invitedProviders.parcel_accounts;

  return {
    ...invitedProviders,
    providers,
    parcel_accounts: parcelAccounts,
  };
}

export function normalizeLoadedDraftData(data: Record<string, unknown>): Record<string, unknown> {
  const schedule = normalizeDraftSchedule(data.schedule as Record<string, unknown> | undefined);
  const service = normalizeServiceForForm(data.service as Record<string, unknown> | undefined);
  let shipment = normalizeShipmentForForm(data.shipment as Record<string, unknown> | undefined);
  const quickRequest = data.quick_request as Record<string, unknown> | undefined;
  const invitedProviders = normalizeInvitedProvidersForForm(data.invited_providers as Record<string, unknown> | undefined);

  if (quickRequest) {
    shipment = {
      ...(shipment ?? { title: "", comments: "" }),
      ...(quickRequest.title !== undefined ? { title: quickRequest.title ?? "" } : {}),
      ...(quickRequest.comments !== undefined ? { comments: quickRequest.comments ?? "" } : {}),
    };
  }

  const { quick_request: _quickRequest, ...rest } = data;

  return {
    ...rest,
    ...(service && { service }),
    ...(shipment && { shipment }),
    ...(invitedProviders && { invited_providers: invitedProviders }),
    schedule,
  };
}
